import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import {
  ServerIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  CircleStackIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { Role } from '../../types';

// バックアップ情報の型定義
interface BackupStatus {
  lastUpdated: string;
  overallStatus: 'healthy' | 'warning' | 'critical';
  backups: BackupItem[];
  schedule: BackupSchedule[];
  storage: StorageInfo;
}

interface BackupItem {
  id: string;
  type: 'database' | 'files' | 'system' | 'audit_logs';
  name: string;
  status: 'success' | 'failed' | 'in_progress' | 'scheduled';
  createdAt: string;
  size: number; // bytes
  location: string;
  retentionDays: number;
  nextBackup?: string;
  details?: string;
}

interface BackupSchedule {
  id: string;
  name: string;
  type: 'database' | 'files' | 'system' | 'audit_logs';
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string;
  enabled: boolean;
  lastRun?: string;
  nextRun: string;
}

interface StorageInfo {
  totalUsed: number; // GB
  totalCapacity: number; // GB
  locations: {
    name: string;
    used: number;
    capacity: number;
  }[];
}

const BackupStatusPage: React.FC = () => {
  const { user } = useAuth();
  const [backupStatus, setBackupStatus] = useState<BackupStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadBackupStatus = async () => {
    // 仮のバックアップステータスデータ（実装準備中）
    const mockBackupStatus: BackupStatus = {
      lastUpdated: new Date().toISOString(),
      overallStatus: 'healthy',
      backups: [
        {
          id: '1',
          type: 'database',
          name: 'Supabase定期バックアップ',
          status: 'success',
          createdAt: '2025-08-12T03:00:00Z',
          size: 2.5 * 1024 * 1024 * 1024, // 2.5GB
          location: 'supabase-backup-jp',
          retentionDays: 30,
          nextBackup: '2025-08-13T03:00:00Z',
        },
        {
          id: '2',
          type: 'files',
          name: '画像ファイルバックアップ',
          status: 'success',
          createdAt: '2025-08-12T02:30:00Z',
          size: 850 * 1024 * 1024, // 850MB
          location: 'supabase-storage-jp',
          retentionDays: 90,
          nextBackup: '2025-08-13T02:30:00Z',
        },
        {
          id: '3',
          type: 'audit_logs',
          name: '監査ログバックアップ',
          status: 'failed',
          createdAt: '2025-08-12T01:00:00Z',
          size: 0,
          location: 'backup-logs-jp',
          retentionDays: 365,
          nextBackup: '2025-08-12T13:00:00Z',
          details: 'アクセス権限エラーが発生しました',
        },
        {
          id: '4',
          type: 'system',
          name: 'システム設定バックアップ',
          status: 'in_progress',
          createdAt: '2025-08-12T09:00:00Z',
          size: 15 * 1024 * 1024, // 15MB
          location: 'system-config-jp',
          retentionDays: 60,
        },
      ],
      schedule: [
        {
          id: 'daily-db',
          name: 'データベース日次バックアップ',
          type: 'database',
          frequency: 'daily',
          time: '03:00',
          enabled: true,
          lastRun: '2025-08-12T03:00:00Z',
          nextRun: '2025-08-13T03:00:00Z',
        },
        {
          id: 'daily-files',
          name: 'ファイル日次バックアップ',
          type: 'files',
          frequency: 'daily',
          time: '02:30',
          enabled: true,
          lastRun: '2025-08-12T02:30:00Z',
          nextRun: '2025-08-13T02:30:00Z',
        },
        {
          id: 'weekly-audit',
          name: '監査ログ週次バックアップ',
          type: 'audit_logs',
          frequency: 'weekly',
          time: '01:00',
          enabled: false,
          lastRun: '2025-08-05T01:00:00Z',
          nextRun: '2025-08-19T01:00:00Z',
        },
      ],
      storage: {
        totalUsed: 15.2,
        totalCapacity: 100,
        locations: [
          { name: 'Supabaseメインストレージ', used: 8.5, capacity: 50 },
          { name: 'バックアップストレージ', used: 6.7, capacity: 50 },
        ],
      },
    };

    await new Promise(resolve => setTimeout(resolve, 1000));
    setBackupStatus(mockBackupStatus);
  };

  useEffect(() => {
    loadBackupStatus().finally(() => setIsLoading(false));
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadBackupStatus();
    setRefreshing(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
      case 'healthy':
        return 'text-green-600 bg-green-50';
      case 'warning':
      case 'scheduled':
        return 'text-yellow-600 bg-yellow-50';
      case 'failed':
      case 'critical':
        return 'text-red-600 bg-red-50';
      case 'in_progress':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
      case 'healthy':
        return <CheckCircleIcon className='h-4 w-4' />;
      case 'warning':
      case 'failed':
      case 'critical':
        return <ExclamationTriangleIcon className='h-4 w-4' />;
      case 'in_progress':
        return <ArrowPathIcon className='h-4 w-4 animate-spin' />;
      case 'scheduled':
        return <ClockIcon className='h-4 w-4' />;
      default:
        return <InformationCircleIcon className='h-4 w-4' />;
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case 'success':
        return '成功';
      case 'failed':
        return '失敗';
      case 'in_progress':
        return '実行中';
      case 'scheduled':
        return '予定済み';
      case 'healthy':
        return '正常';
      case 'warning':
        return '注意';
      case 'critical':
        return '要対応';
      default:
        return '不明';
    }
  };

  const getTypeDisplayName = (type: string) => {
    switch (type) {
      case 'database':
        return 'データベース';
      case 'files':
        return 'ファイル';
      case 'system':
        return 'システム設定';
      case 'audit_logs':
        return '監査ログ';
      default:
        return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'database':
        return <CircleStackIcon className='h-5 w-5' />;
      case 'files':
        return <DocumentDuplicateIcon className='h-5 w-5' />;
      case 'system':
        return <ServerIcon className='h-5 w-5' />;
      case 'audit_logs':
        return <DocumentDuplicateIcon className='h-5 w-5' />;
      default:
        return <ServerIcon className='h-5 w-5' />;
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatFrequency = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return '毎日';
      case 'weekly':
        return '毎週';
      case 'monthly':
        return '毎月';
      default:
        return frequency;
    }
  };

  // super_admin権限チェック
  if (!user || user.role !== Role.SUPER_ADMIN) {
    return <Navigate to='/' replace />;
  }

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  if (!backupStatus) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
        バックアップ情報の取得に失敗しました
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      {/* ヘッダー */}
      <div className='bg-white dark:bg-gray-800 shadow'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-6'>
            <div>
              <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>バックアップ状況</h1>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                データ保護とバックアップ管理の監視
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className='inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50'
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? '更新中...' : '最新情報に更新'}
            </button>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* 実装準備中メッセージ */}
        <div className='mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
          <div className='flex items-center'>
            <InformationCircleIcon className='h-5 w-5 text-blue-400 mr-2' />
            <p className='text-sm text-blue-800 dark:text-blue-200'>
              <strong>実装準備中:</strong>{' '}
              現在はサンプルデータを表示しています。実際のバックアップ管理機能はSprint
              4で実装予定です。
            </p>
          </div>
        </div>

        {/* 総合ステータスとストレージ情報 */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8'>
          {/* 総合ステータス */}
          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-6'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>{getStatusIcon(backupStatus.overallStatus)}</div>
                  <div className='ml-4'>
                    <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
                      バックアップ総合ステータス
                    </h3>
                    <p
                      className={`text-sm font-medium ${getStatusColor(backupStatus.overallStatus)}`}
                    >
                      {getStatusDisplayName(backupStatus.overallStatus)}
                    </p>
                  </div>
                </div>
                <div className='text-right'>
                  <div className='flex items-center text-sm text-gray-500 dark:text-gray-400'>
                    <ClockIcon className='h-4 w-4 mr-1' />
                    最終更新: {new Date(backupStatus.lastUpdated).toLocaleString('ja-JP')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ストレージ使用量 */}
          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-6'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <CloudArrowUpIcon className='h-8 w-8 text-purple-600' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                      ストレージ使用量
                    </dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                      {backupStatus.storage.totalUsed}GB / {backupStatus.storage.totalCapacity}GB
                    </dd>
                  </dl>
                  <div className='mt-2'>
                    <div className='bg-gray-200 rounded-full h-2'>
                      <div
                        className='bg-purple-600 h-2 rounded-full'
                        style={{
                          width: `${(backupStatus.storage.totalUsed / backupStatus.storage.totalCapacity) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* バックアップ履歴 */}
        <div className='mb-8'>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white mb-4'>
            最新バックアップ履歴
          </h2>
          <div className='bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden'>
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                <thead className='bg-gray-50 dark:bg-gray-700'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      バックアップ名
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      種類
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      ステータス
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      サイズ
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      作成日時
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      次回予定
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                  {backupStatus.backups.map(backup => (
                    <tr key={backup.id} className='hover:bg-gray-50 dark:hover:bg-gray-700'>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='flex items-center'>
                          {getTypeIcon(backup.type)}
                          <div className='ml-3'>
                            <div className='text-sm font-medium text-gray-900 dark:text-white'>
                              {backup.name}
                            </div>
                            <div className='text-sm text-gray-500 dark:text-gray-400'>
                              {backup.location}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                        {getTypeDisplayName(backup.type)}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(backup.status)}`}
                        >
                          {getStatusIcon(backup.status)}
                          <span className='ml-1'>{getStatusDisplayName(backup.status)}</span>
                        </span>
                        {backup.details && (
                          <div className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                            {backup.details}
                          </div>
                        )}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                        {formatBytes(backup.size)}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                        {new Date(backup.createdAt).toLocaleString('ja-JP')}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                        {backup.nextBackup
                          ? new Date(backup.nextBackup).toLocaleString('ja-JP')
                          : '-'}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                        <button
                          className='text-primary-600 hover:text-primary-500 disabled:opacity-50'
                          disabled
                          onClick={() => {
                            // ダウンロード機能（未実装）
                            alert('ダウンロード機能は実装準備中です');
                          }}
                        >
                          <CloudArrowDownIcon className='h-4 w-4' />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* バックアップスケジュール */}
        <div>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white mb-4'>
            バックアップスケジュール
          </h2>
          <div className='bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden'>
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                <thead className='bg-gray-50 dark:bg-gray-700'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      スケジュール名
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      種類
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      頻度
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      実行時間
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      最終実行
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      次回実行
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      ステータス
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                  {backupStatus.schedule.map(schedule => (
                    <tr key={schedule.id} className='hover:bg-gray-50 dark:hover:bg-gray-700'>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='flex items-center'>
                          {getTypeIcon(schedule.type)}
                          <div className='ml-3'>
                            <div className='text-sm font-medium text-gray-900 dark:text-white'>
                              {schedule.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                        {getTypeDisplayName(schedule.type)}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                        {formatFrequency(schedule.frequency)}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                        {schedule.time}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                        {schedule.lastRun
                          ? new Date(schedule.lastRun).toLocaleString('ja-JP')
                          : '-'}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                        {new Date(schedule.nextRun).toLocaleString('ja-JP')}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${schedule.enabled ? 'text-green-600 bg-green-50' : 'text-gray-600 bg-gray-50'}`}
                        >
                          {schedule.enabled ? '有効' : '無効'}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                        <button
                          className='text-primary-600 hover:text-primary-500 mr-3 disabled:opacity-50'
                          disabled
                          onClick={() => {
                            // 即座実行機能（未実装）
                            alert('即座実行機能は実装準備中です');
                          }}
                        >
                          <PlayIcon className='h-4 w-4' />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupStatusPage;
